import { computed, inject, signal, effect } from '@angular/core';
import { Store } from '@ngxs/store';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { BusinessUser, BusinessAddress } from '@models/businesses';
import { BusinessAddressesState } from '@state/business/address/address.state';
import { BusinessDispatch } from '@state/business';
import { BusinessAddressesDispatch } from '@state/business/address';
import { BusinessState } from '@state/business';
import { BusinessUsersService } from '@repos/businesses/users/business-users.service';
import { isSet } from '@globalUtils/utilities';

const SINGULAR = 'Singular';
const PLURAL = 'Plural';

export class BarristerSolicitorFormatDrawerViewModel {
	private store = inject(Store);
	private businessDispatch = inject(BusinessDispatch);
	private businessAddressesDispatch = inject(BusinessAddressesDispatch);
	private businessUsersService = inject(BusinessUsersService);

	lawyers = this.store.selectSignal(BusinessUsersState.getLawyers);
	offices = this.store.selectSignal(BusinessAddressesState.getAll);
	business = this.store.selectSignal(BusinessState.getBusiness);

	selectedSettingType = 'firm';
	tooltipText =
		"Singular: 'Barrister & Solicitor' – Used when documents should state 'I am the lawyer of'\n\nPlural: 'Barristers & Solicitors' – Used when documents should state 'We are the solicitors of'";

	constructor() {
		effect(
			() => {
				const existingOverrides = this.lawyers().reduce(
					(acc, lawyer) => {
						if (isSet(lawyer.useSingularBarristerSolicitor)) {
							acc.push({
								lawyer,
								pronoun: lawyer.useSingularBarristerSolicitor
									? SINGULAR
									: PLURAL,
							});
						}
						return acc;
					},
					[] as { lawyer: BusinessUser; pronoun: string }[],
				);

				this.lawyerOverrides.set(existingOverrides);
			},
			{ allowSignalWrites: true },
		);
	}

	firmPronounValue = computed(() =>
		this.business()?.defaultSettings?.useSingularBarristerSolicitor
			? SINGULAR
			: PLURAL,
	);

	officePronounValues = computed(() => {
		const offices = this.offices();
		return offices.reduce(
			(acc, office) => {
				acc[office.id] =
					office.useSingularBarristerSolicitor === true
						? SINGULAR
						: office.useSingularBarristerSolicitor === false
							? PLURAL
							: null;
				return acc;
			},
			{} as { [key: string]: string | null },
		);
	});

	lawyerDropdownItems = computed(() => [
		{
			title: 'Lawyers',
			items: this.lawyers().map((lawyer) => ({
				value: lawyer,
				name: `${lawyer.firstName} ${lawyer.lastName}`,
			})),
		},
	]);

	selectedLawyerName = computed(() => {
		const lawyer = this.selectedLawyer();
		return lawyer ? `${lawyer.firstName} ${lawyer.lastName}` : null;
	});

	selectedLawyer = signal<BusinessUser | null>(null);
	selectedLawyerPronoun = signal<string | null>(null);
	lawyerOverrides = signal<{ lawyer: BusinessUser; pronoun: string }[]>([]);

	onSelectLawyer(event: { value: BusinessUser }) {
		this.selectedLawyer.set(event.value);
	}

	onSelectLawyerPronoun(pronoun: string) {
		this.selectedLawyerPronoun.set(pronoun);
	}

	onAddOverride() {
		const lawyer = this.selectedLawyer();
		const pronoun = this.selectedLawyerPronoun();

		if (!lawyer || !pronoun) return;

		const useSingular = pronoun === SINGULAR;
		this.businessUsersService.Update(lawyer.id!, {
			useSingularBarristerSolicitor: useSingular,
		});

		this.lawyerOverrides.update((overrides) => [
			...overrides,
			{ lawyer, pronoun },
		]);

		this.selectedLawyer.set(null);
		this.selectedLawyerPronoun.set(null);
	}

	onDeleteLawyerOverride(lawyerId: string) {
		this.businessUsersService.Update(lawyerId, {
			useSingularBarristerSolicitor: null,
		});

		this.lawyerOverrides.update((overrides) =>
			overrides.filter((o) => o.lawyer.id !== lawyerId),
		);
	}

	onSelectOfficePronoun(office: BusinessAddress, pronoun: string) {
		const useSingular = pronoun === 'Singular';
		this.businessAddressesDispatch.UpdateAll([
			{
				id: office.id,
				useSingularBarristerSolicitor: useSingular,
			},
		]);
	}

	onSelectFirmPronoun(pronoun: string) {
		const useSingular = pronoun === SINGULAR;

		this.businessDispatch.OverwriteBusinessFields(
			'defaultSettings.useSingularBarristerSolicitor',
			useSingular,
		);
	}
}
